/**
 * Ring buffer for per-frame timing diagnostics.
 *
 * Module-level singleton (same pattern as ClientPrediction's Rapier world).
 * Pre-allocated array of FrameSample objects overwritten in-place (zero
 * allocation per frame in recordFrame).
 *
 * Used to diagnose frame timing issues (e.g. Firefox 140fps on 165Hz display).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FrameSample {
  timestamp: number;
  elapsed: number;
  accBefore: number;
  accAfter: number;
  ticksThisFrame: number;
  renderAlpha: number;
  wasSnapshot: boolean;
  reconcileMs: number;
}

export interface FrameTimingStats {
  fps: { avg: number; min: number; max: number };
  frameDelta: { avg: number; min: number; max: number; stddev: number; p95: number; p99: number };
  ticksHistogram: { 0: number; 1: number; 2: number; 3: number; '4+': number };
  stutterCount: number;
  estimatedRefreshRate: number;
}

export interface LiveFrameMetrics {
  frameTimeMs: number;
  ticksLastFrame: number;
  stutterCount: number;
}

export interface BrowserInfo {
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  windowWidth: number;
  windowHeight: number;
  devicePixelRatio: number;
  refreshRate: number | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const BUFFER_SIZE = 300;
export const EMA_ALPHA = 0.1;
const STUTTER_MULTIPLIER = 1.5;
const WARMUP_SAMPLES = 10;

// ---------------------------------------------------------------------------
// Module state (singleton)
// ---------------------------------------------------------------------------

let buffer: FrameSample[] = [];
let writeIndex = 0;
let sampleCount = 0;
let emaFrameTime = 0;
let stutterCount = 0;
let lastTicksThisFrame = 0;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pre-allocate the ring buffer and reset all counters.
 * Call once at game loop start.
 */
export function initFrameTimingBuffer(): void {
  buffer = new Array<FrameSample>(BUFFER_SIZE);
  for (let i = 0; i < BUFFER_SIZE; i++) {
    buffer[i] = {
      timestamp: 0,
      elapsed: 0,
      accBefore: 0,
      accAfter: 0,
      ticksThisFrame: 0,
      renderAlpha: 0,
      wasSnapshot: false,
      reconcileMs: 0,
    };
  }
  writeIndex = 0;
  sampleCount = 0;
  emaFrameTime = 0;
  stutterCount = 0;
  lastTicksThisFrame = 0;
}

/**
 * Record one frame's timing data. Zero allocations — overwrites in-place.
 */
export function recordFrame(
  timestamp: number,
  elapsed: number,
  accBefore: number,
  accAfter: number,
  ticksThisFrame: number,
  renderAlpha: number,
  wasSnapshot: boolean,
  reconcileMs: number,
): void {
  const sample = buffer[writeIndex];
  sample.timestamp = timestamp;
  sample.elapsed = elapsed;
  sample.accBefore = accBefore;
  sample.accAfter = accAfter;
  sample.ticksThisFrame = ticksThisFrame;
  sample.renderAlpha = renderAlpha;
  sample.wasSnapshot = wasSnapshot;
  sample.reconcileMs = reconcileMs;

  // Update EMA
  if (sampleCount === 0) {
    emaFrameTime = elapsed;
  } else {
    emaFrameTime = EMA_ALPHA * elapsed + (1 - EMA_ALPHA) * emaFrameTime;
  }

  // Stutter detection (only after warmup)
  if (sampleCount > WARMUP_SAMPLES && elapsed > STUTTER_MULTIPLIER * emaFrameTime) {
    stutterCount++;
  }

  lastTicksThisFrame = ticksThisFrame;
  sampleCount++;
  writeIndex = (writeIndex + 1) % BUFFER_SIZE;
}

/**
 * Return cheap rolling metrics for DebugPanel display.
 */
export function getLiveFrameMetrics(): LiveFrameMetrics {
  return {
    frameTimeMs: Math.round(emaFrameTime * 100) / 100,
    ticksLastFrame: lastTicksThisFrame,
    stutterCount,
  };
}

/**
 * Compute full statistics from the ring buffer. Expensive — only call at export time.
 */
export function computeFrameTimingStats(): FrameTimingStats {
  const validCount = Math.min(sampleCount, BUFFER_SIZE);
  if (validCount === 0) {
    return {
      fps: { avg: 0, min: 0, max: 0 },
      frameDelta: { avg: 0, min: 0, max: 0, stddev: 0, p95: 0, p99: 0 },
      ticksHistogram: { 0: 0, 1: 0, 2: 0, 3: 0, '4+': 0 },
      stutterCount: 0,
      estimatedRefreshRate: 0,
    };
  }

  // Collect valid samples in chronological order
  const samples: FrameSample[] = [];
  if (sampleCount <= BUFFER_SIZE) {
    for (let i = 0; i < validCount; i++) {
      samples.push(buffer[i]);
    }
  } else {
    // Buffer has wrapped — read from writeIndex (oldest) forward
    for (let i = 0; i < BUFFER_SIZE; i++) {
      samples.push(buffer[(writeIndex + i) % BUFFER_SIZE]);
    }
  }

  // Frame deltas (elapsed values)
  const deltas = samples.map((s) => s.elapsed);
  const sortedDeltas = [...deltas].sort((a, b) => a - b);

  const sum = deltas.reduce((a, b) => a + b, 0);
  const avg = sum / deltas.length;
  const min = sortedDeltas[0];
  const max = sortedDeltas[sortedDeltas.length - 1];

  // Standard deviation
  const sqDiffSum = deltas.reduce((acc, d) => acc + (d - avg) * (d - avg), 0);
  const stddev = Math.sqrt(sqDiffSum / deltas.length);

  // Percentiles
  const p95 = sortedDeltas[Math.floor(sortedDeltas.length * 0.95)];
  const p99 = sortedDeltas[Math.floor(sortedDeltas.length * 0.99)];

  // FPS from deltas
  const fpsAvg = avg > 0 ? 1000 / avg : 0;
  const fpsMin = max > 0 ? 1000 / max : 0; // min fps = max delta
  const fpsMax = min > 0 ? 1000 / min : 0; // max fps = min delta

  // Ticks histogram
  const ticksHistogram = { 0: 0, 1: 0, 2: 0, 3: 0, '4+': 0 };
  for (const s of samples) {
    if (s.ticksThisFrame === 0) ticksHistogram[0]++;
    else if (s.ticksThisFrame === 1) ticksHistogram[1]++;
    else if (s.ticksThisFrame === 2) ticksHistogram[2]++;
    else if (s.ticksThisFrame === 3) ticksHistogram[3]++;
    else ticksHistogram['4+']++;
  }

  // Estimate refresh rate from 0-tick frame intervals.
  // When ticksThisFrame === 0, elapsed ≈ one vsync interval.
  const zeroTickDeltas = samples
    .filter((s) => s.ticksThisFrame === 0)
    .map((s) => s.elapsed);
  let estimatedRefreshRate = 0;
  if (zeroTickDeltas.length > 5) {
    const avgZeroDelta = zeroTickDeltas.reduce((a, b) => a + b, 0) / zeroTickDeltas.length;
    estimatedRefreshRate = avgZeroDelta > 0 ? Math.round(1000 / avgZeroDelta) : 0;
  }

  return {
    fps: { avg: Math.round(fpsAvg * 10) / 10, min: Math.round(fpsMin * 10) / 10, max: Math.round(fpsMax * 10) / 10 },
    frameDelta: {
      avg: Math.round(avg * 100) / 100,
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100,
      stddev: Math.round(stddev * 100) / 100,
      p95: Math.round(p95 * 100) / 100,
      p99: Math.round(p99 * 100) / 100,
    },
    ticksHistogram,
    stutterCount,
    estimatedRefreshRate,
  };
}

/**
 * Return shallow copies of valid samples in chronological order.
 * For debug export only — allocates.
 */
export function getBufferSnapshot(): FrameSample[] {
  const validCount = Math.min(sampleCount, BUFFER_SIZE);
  if (validCount === 0) return [];

  const result: FrameSample[] = [];
  if (sampleCount <= BUFFER_SIZE) {
    for (let i = 0; i < validCount; i++) {
      result.push({ ...buffer[i] });
    }
  } else {
    for (let i = 0; i < BUFFER_SIZE; i++) {
      result.push({ ...buffer[(writeIndex + i) % BUFFER_SIZE] });
    }
  }
  return result;
}

/**
 * Capture browser and display environment info.
 */
export function getBrowserInfo(): BrowserInfo {
  return {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    screenWidth: typeof screen !== 'undefined' ? screen.width : 0,
    screenHeight: typeof screen !== 'undefined' ? screen.height : 0,
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    windowHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    refreshRate: null, // Not reliably available in all browsers
  };
}
